import type { AccountsPayableFilters } from './purchases.service'

import { http } from '@/app/lib/http/client'

export type AccountsPayableExportFilters = Omit<AccountsPayableFilters, 'page' | 'per_page' | 'status'> & { limit?: number }
export type ReportEmailStatus = 'queued' | 'generating' | 'ready' | 'sending' | 'sent' | 'failed' | 'delivery_unknown'
export interface ReportEmailRequestResult {
  id: string
  report: string
  status: ReportEmailStatus
  error_message: string | null
  created_at: string
  generated_at: string | null
  sent_at: string | null
  failed_at: string | null
  recipient_count: number
  sender_sucursal_id: number | null
}
export interface ReportEmailRecipientOption {
  id: number
  name: string
  email: string
}
export interface ReportEmailSenderOption {
  sucursal_id: number
  sucursal_name: string
  address: string | null
  name: string
  configured: boolean
}
export interface ReportEmailRecipientsResult {
  data: ReportEmailRecipientOption[]
  current_page: number
  last_page: number
  total: number
  sender: ReportEmailSenderOption
}
export interface SendReportEmailRequest {
  report: string
  filters: Record<string, unknown>
  limit?: number
  recipient_ids: number[]
  subject: string
  description: string
  idempotency_key: string
}

export interface ReportFilters {
  date_from?: string
  date_to?: string
  sucursal_id?: number
  status?: string
  currency?: string
  payment_method_id?: number
  search?: string
  page?: number
  per_page?: number
}

export type SalesReportType =
  | 'general'
  | 'department-category'
  | 'products'
  | 'profits'
  | 'users'
  | 'sellers'
  | 'customers'

export interface SalesReportFilters extends Record<string, unknown> {
  date_from?: string
  date_to?: string
  sucursal_id?: number
  status?: string
  currency?: 'MXN'
  payment_method_id?: number
  with_taxes?: boolean
  department_id?: number
  category_id?: number
  product_id?: number
  customer_id?: number
  include_returns?: boolean
  group_average?: boolean
  document?: 'sales'
  discount?: 'all' | 'with' | 'without'
  sort_by?:
    | 'sales'
    | 'net_total'
    | 'sales_count'
    | 'cancelled_count'
    | 'discounts'
    | 'average'
  page?: number
  limit?: number
}

export interface SalesReportResult<Row = Record<string, unknown>> {
  report: SalesReportType
  columns?: Array<{
    key: string
    label: string
    type:
      | 'text'
      | 'integer'
      | 'decimal'
      | 'money'
      | 'percentage'
      | 'date'
      | 'date-time'
    group: 'fixed' | 'tax' | 'payment'
  }>
  rows: Row[]
  totals: Record<string, string | number | null>
  pagination: {
    current_page: number
    last_page: number
    per_page: 10
    total: number
    from: number | null
    to: number | null
  }
}

export interface SalesFilterOption {
  id?: number
  value?: string
  name?: string
  label?: string
  code?: string
  disabled?: boolean
}

export interface SalesReportFilterOptions {
  sucursals: SalesFilterOption[]
  statuses: SalesFilterOption[]
  currencies: SalesFilterOption[]
  payment_methods: SalesFilterOption[]
  departments: SalesFilterOption[]
  categories: SalesFilterOption[]
  users: SalesFilterOption[]
  sellers: SalesFilterOption[]
  documents: SalesFilterOption[]
  sort_options: SalesFilterOption[]
}

export type InventoryReportType =
  | 'stocks'
  | 'min-max'
  | 'cofepris'
  | 'batches'
  | 'kardex'

export interface InventoryReportFilters extends Record<string, unknown> {
  date_from?: string
  date_to?: string
  sucursal_id?: number
  department_id?: number
  category_id?: number
  product_id?: number
  user_id?: number
  ticket?: string
  doctor?: string
  regulatory_class?: 'ANTIBIOTIC' | 'CONTROLLED'
  fulfillment_type?: 'COMPLETE' | 'PARTIAL'
  stock_status?:
    | 'all'
    | 'zero'
    | 'negative'
    | 'positive'
    | 'below-min'
    | 'above-max'
  batch_status?: 'all' | 'active' | 'expired' | 'near-expiry'
  document?:
    | 'all'
    | 'sales'
    | 'purchases'
    | 'transfers'
    | 'imports'
    | 'manual-adjustments'
  show_utilities?: boolean
  page?: number
  limit?: number
}

export interface InventoryReportResult<Row = Record<string, unknown>> {
  report: InventoryReportType
  rows: Row[]
  totals: Record<string, string | number | null>
  pagination: SalesReportResult['pagination']
}

export interface InventoryReportFilterOptions {
  sucursals: SalesFilterOption[]
  departments: SalesFilterOption[]
  categories: SalesFilterOption[]
  stock_statuses: SalesFilterOption[]
  batch_statuses: SalesFilterOption[]
  regulatory_classes: SalesFilterOption[]
  fulfillment_types: SalesFilterOption[]
  doctors: SalesFilterOption[]
  users: SalesFilterOption[]
  documents: SalesFilterOption[]
}

export interface CashMovementReportFilters extends Record<string, unknown> {
  date_from?: string
  date_to?: string
  direction?: 'all' | 'in' | 'out'
  movement?: 'all' | 'sales' | 'purchases' | 'cash-register'
  sucursal_id?: number
  cash_register_id?: number
  payment_method_id?: number
  page?: number
  limit?: number
}

export interface CashMovementReportResult<Row = Record<string, unknown>> {
  report: 'cash-movements'
  rows: Row[]
  totals: Record<string, string | number | null>
  pagination: SalesReportResult['pagination']
}

export interface CashMovementReportFilterOptions {
  sucursals: SalesFilterOption[]
  cash_registers: CashRegisterFilterOption[]
  directions: SalesFilterOption[]
  movements: SalesFilterOption[]
  payment_methods: SalesFilterOption[]
}

export type CashRegisterReportType = 'balances' | 'closures'

export interface CashRegisterReportFilters extends Record<string, unknown> {
  date_from?: string
  date_to?: string
  result?: 'all' | 'with-difference' | 'negative' | 'positive' | 'exact'
  sucursal_id?: number
  cash_register_id?: number
  folio?: string
  user_id?: number
  page?: number
  limit?: number
}

export interface CashRegisterReportResult<Row = unknown> {
  report: CashRegisterReportType
  rows: Row[]
  totals: Record<string, string | number | null>
  pagination: SalesReportResult['pagination']
}

export interface CashRegisterFilterOption extends SalesFilterOption {
  sucursal_id?: number
  branch?: string
  is_active?: boolean
}

export interface CashRegisterReportFilterOptions {
  sucursals: SalesFilterOption[]
  cash_registers: CashRegisterFilterOption[]
  results: SalesFilterOption[]
  users: SalesFilterOption[]
}

export interface SaleReturnReportFilters extends Record<string, unknown> {
  date_from?: string
  date_to?: string
  sucursal_id?: number
  status?: 'all' | 'active' | 'cancelled'
  payment_method_id?: number
  page?: number
  limit?: number
}

export interface SaleReturnReportRow {
  ticket: string
  branch: string
  user: string
  discount: number
  amount: number
}

export interface SaleReturnReportResult {
  report: 'sale-returns'
  rows: SaleReturnReportRow[]
  totals: {
    record_count: number
    discount: number
    amount: number
  }
  pagination: SalesReportResult['pagination']
}

export interface SaleReturnReportFilterOptions {
  sucursals: SalesFilterOption[]
  statuses: SalesFilterOption[]
  payment_methods: SalesFilterOption[]
}

export type OrderReportType = 'general' | 'delivery-users'

export type OrderReportSort =
  | 'orders'
  | 'net_total'
  | 'order_count'
  | 'cancelled_count'

export interface OrderReportFilters extends Record<string, unknown> {
  date_from?: string
  date_to?: string
  sort_by?: OrderReportSort
  page?: number
  limit?: number
}

export interface GeneralOrderReportRow {
  ticket: string
  branch: string
  user: string
  discount: number
  amount: number
}

export interface DeliveryUserOrderReportRow {
  delivery_user: string
  order_count: number
  cancelled_count: number
  average: number
  discount: number
}

export interface OrderReportResult<Row = Record<string, unknown>> {
  report: OrderReportType
  rows: Row[]
  totals: {
    record_count: number
    order_count?: number
    cancelled_count?: number
    average?: number
    discount: number
    amount?: number
  }
  pagination: SalesReportResult['pagination']
}

export interface OrderReportFilterOptions {
  statuses: SalesFilterOption[]
  sort_options: SalesFilterOption[]
}

export type PurchaseReportType = 'general' | 'products' | 'suppliers'

export interface PurchaseReportFilters extends Record<string, unknown> {
  date_from?: string
  date_to?: string
  sucursal_id?: number
  supplier_id?: number
  payment_method_id?: number
  product_id?: number
  department_id?: number
  category_id?: number
  page?: number
  limit?: number
}

export interface GeneralPurchaseReportRow {
  purchase_date: string
  entry_date: string
  created_at: string | null
  folio: string
  source: string
  supplier: string
  branch: string
  user: string
  user_account: string
  payment_method: string
  discount: number | null
  discount_percentage: number | null
  amount: number | null
}

export interface ProductPurchaseReportRow {
  barcode: string
  product: string
  department: string
  category: string
  quantity: number
  unit: string
  average_price: number | null
  amount: number | null
}

export interface SupplierPurchaseReportRow {
  supplier: string
  purchase_count: number
  average: number | null
  discount: number | null
  discount_percentage: number | null
  amount: number | null
}

export interface PurchaseReportResult<Row = Record<string, unknown>> {
  report: PurchaseReportType
  rows: Row[]
  totals: {
    record_count: number
    purchase_count?: number
    quantity?: number
    discount?: number | null
    discount_percentage?: number | null
    amount?: number | null
    average_price?: number | null
    average?: number | null
    unavailable_amount_count: number
    unavailable_discount_count?: number
  }
  pagination: SalesReportResult['pagination']
}

export interface PurchaseReportFilterOptions {
  sucursals: SalesFilterOption[]
  payment_methods: SalesFilterOption[]
  departments: SalesFilterOption[]
  categories: SalesFilterOption[]
}

export type CfdiReportType = 'invoices' | 'credit-notes'
export type CfdiReportStatus = 'all' | 'active' | 'canceled' | 'pending'

export interface CfdiReportFilters extends Record<string, unknown> {
  date_from?: string
  date_to?: string
  status?: CfdiReportStatus
  page?: number
  limit?: number
}

export interface CfdiReportRow {
  date: string
  fiscal_uuid: string
  folio: string
  customer: string
  receiver_rfc: string
  status: 'Vigente' | 'Cancelado' | 'Pendiente'
  subtotal: number
  payment_method: string
  user: string
  total: number
}

export interface CfdiReportResult {
  report: CfdiReportType
  rows: CfdiReportRow[]
  totals: {
    record_count: number
    subtotal: number
    total: number
  }
  pagination: SalesReportResult['pagination']
}

export interface BaseReportData {
  id: number
  reference: string
  total?: number
}

export interface ReportResponse<T = BaseReportData> {
  data: T[]
  summary?: Record<string, string | number>
  meta?: {
    current_page?: number
    last_page?: number
    total?: number
  }
}

async function getReport<T>(
  endpoint: string,
  filters?: ReportFilters,
): Promise<ReportResponse<T>> {
  const response = await http.get(endpoint, { params: filters })
  return response.data?.data || response.data
}

export const reportsService = {
  async getReportEmailRecipients(
    search = '',
    page = 1,
  ): Promise<ReportEmailRecipientsResult> {
    const response = await http.get('/reports/emails/recipients', {
      params: { search, page },
    })
    return response.data?.data
  },

  async sendReportEmail(
    payload: SendReportEmailRequest,
  ): Promise<ReportEmailRequestResult> {
    const response = await http.post('/reports/emails', payload)
    return response.data?.data
  },

  async getReportEmailRequest(
    id: string,
  ): Promise<ReportEmailRequestResult> {
    const response = await http.get(`/reports/emails/${id}`)
    return response.data?.data
  },

  async getSalesReport<Row = Record<string, unknown>>(
    type: SalesReportType,
    filters?: SalesReportFilters,
  ): Promise<SalesReportResult<Row>> {
    const response = await http.get(`/reports/sales/${type}`, {
      params: filters,
    })
    return response.data?.data
  },

  async getSalesFilterOptions(): Promise<SalesReportFilterOptions> {
    const response = await http.get('/reports/sales/filter-options')
    return response.data?.data
  },

  async exportSalesReport(
    type: SalesReportType,
    filters: SalesReportFilters,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await http.post(`/reports/sales/${type}/export`, filters, {
      responseType: 'blob',
      timeout: 120000,
    })
    const disposition = String(response.headers['content-disposition'] || '')
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]

    return {
      blob: response.data,
      filename: encoded
        ? decodeURIComponent(encoded)
        : plain || `reporte-ventas-${type}.xlsx`,
    }
  },

  async getInventoryReport<Row = Record<string, unknown>>(
    type: InventoryReportType,
    filters?: InventoryReportFilters,
  ): Promise<InventoryReportResult<Row>> {
    const response = await http.get(`/reports/inventory/${type}`, {
      params: filters,
    })
    return response.data?.data
  },

  async getInventoryFilterOptions(): Promise<InventoryReportFilterOptions> {
    const response = await http.get('/reports/inventory/filter-options')
    return response.data?.data
  },

  async exportInventoryReport(
    type: InventoryReportType,
    filters: InventoryReportFilters,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await http.post(
      `/reports/inventory/${type}/export`,
      filters,
      {
        responseType: 'blob',
        timeout: 120000,
      },
    )
    const disposition = String(response.headers['content-disposition'] || '')
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]

    return {
      blob: response.data,
      filename: encoded
        ? decodeURIComponent(encoded)
        : plain || `reporte-inventario-${type}.xlsx`,
    }
  },

  async getCashMovementsReport<Row = Record<string, unknown>>(
    filters?: CashMovementReportFilters,
  ): Promise<CashMovementReportResult<Row>> {
    const response = await http.get('/reports/cash-movements', {
      params: filters,
    })
    return response.data?.data
  },

  async getCashMovementFilterOptions(): Promise<CashMovementReportFilterOptions> {
    const response = await http.get('/reports/cash-movements/filter-options')
    return response.data?.data
  },

  async exportCashMovementsReport(
    filters: CashMovementReportFilters,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await http.post(
      '/reports/cash-movements/export',
      filters,
      {
        responseType: 'blob',
        timeout: 120000,
      },
    )
    const disposition = String(response.headers['content-disposition'] || '')
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]

    return {
      blob: response.data,
      filename: encoded
        ? decodeURIComponent(encoded)
        : plain || 'reporte-movimientos-dinero.xlsx',
    }
  },

  async getCashRegisterReport<Row = unknown>(
    type: CashRegisterReportType,
    filters?: CashRegisterReportFilters,
  ): Promise<CashRegisterReportResult<Row>> {
    const response = await http.get(`/reports/cash-registers/${type}`, {
      params: filters,
    })
    return response.data?.data
  },

  async getCashRegisterFilterOptions(): Promise<CashRegisterReportFilterOptions> {
    const response = await http.get('/reports/cash-registers/filter-options')
    return response.data?.data
  },

  async exportCashRegisterReport(
    type: CashRegisterReportType,
    filters: CashRegisterReportFilters,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await http.post(
      `/reports/cash-registers/${type}/export`,
      filters,
      {
        responseType: 'blob',
        timeout: 120000,
      },
    )
    const disposition = String(response.headers['content-disposition'] || '')
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]

    return {
      blob: response.data,
      filename: encoded
        ? decodeURIComponent(encoded)
        : plain || `reporte-cajas-${type}.xlsx`,
    }
  },

  async getSaleReturnReport(
    filters?: SaleReturnReportFilters,
  ): Promise<SaleReturnReportResult> {
    const response = await http.get('/reports/sale-returns', {
      params: filters,
    })
    return response.data?.data
  },

  async getSaleReturnFilterOptions(): Promise<SaleReturnReportFilterOptions> {
    const response = await http.get('/reports/sale-returns/filter-options')
    return response.data?.data
  },

  async exportSaleReturnReport(
    filters: SaleReturnReportFilters,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await http.post('/reports/sale-returns/export', filters, {
      responseType: 'blob',
      timeout: 120000,
    })
    const disposition = String(response.headers['content-disposition'] || '')
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]

    return {
      blob: response.data,
      filename: encoded
        ? decodeURIComponent(encoded)
        : plain || 'reporte-devoluciones.xlsx',
    }
  },

  async getOrderReport<Row = Record<string, unknown>>(
    type: OrderReportType,
    filters?: OrderReportFilters,
  ): Promise<OrderReportResult<Row>> {
    const response = await http.get(`/reports/orders/${type}`, {
      params: filters,
    })
    return response.data?.data
  },

  async getOrderFilterOptions(): Promise<OrderReportFilterOptions> {
    const response = await http.get('/reports/orders/filter-options')
    return response.data?.data
  },

  async exportOrderReport(
    type: OrderReportType,
    filters: OrderReportFilters,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await http.post(
      `/reports/orders/${type}/export`,
      filters,
      {
        responseType: 'blob',
        timeout: 120000,
      },
    )
    const disposition = String(response.headers['content-disposition'] || '')
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]

    return {
      blob: response.data,
      filename: encoded
        ? decodeURIComponent(encoded)
        : plain || `reporte-pedidos-${type}.xlsx`,
    }
  },

  async getPurchaseReport<Row = Record<string, unknown>>(
    type: PurchaseReportType,
    filters?: PurchaseReportFilters,
  ): Promise<PurchaseReportResult<Row>> {
    const response = await http.get(`/reports/purchases/${type}`, {
      params: filters,
    })
    return response.data?.data
  },

  async getPurchaseFilterOptions(): Promise<PurchaseReportFilterOptions> {
    const response = await http.get('/reports/purchases/filter-options')
    return response.data?.data
  },

  async exportPurchaseReport(
    type: PurchaseReportType | 'accounts-payable',
    filters: PurchaseReportFilters | AccountsPayableExportFilters,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await http.post(
      `/reports/purchases/${type}/export`,
      filters,
      { responseType: 'blob', timeout: 120000 },
    )
    const disposition = String(response.headers['content-disposition'] || '')
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]

    return {
      blob: response.data,
      filename: encoded
        ? decodeURIComponent(encoded)
        : plain || `reporte-compras-${type}.xlsx`,
    }
  },

  async getCfdiReport(
    type: CfdiReportType,
    filters?: CfdiReportFilters,
  ): Promise<CfdiReportResult> {
    const response = await http.get(`/reports/cfdi/${type}`, {
      params: filters,
    })
    return response.data?.data
  },

  async exportCfdiReport(
    type: CfdiReportType,
    filters: CfdiReportFilters,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await http.post(`/reports/cfdi/${type}/export`, filters, {
      responseType: 'blob',
      timeout: 120000,
    })
    const disposition = String(response.headers['content-disposition'] || '')
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]

    return {
      blob: response.data,
      filename: encoded
        ? decodeURIComponent(encoded)
        : plain || `reporte-cfdi-${type}.xlsx`,
    }
  },

  getSalesReports: <T = BaseReportData>(filters?: ReportFilters) =>
    getReport<T>('/reports/sales', filters),
}
